import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter, 
  getDocs 
} from 'firebase/firestore';
import { db } from '../../firebase/config';

const TASKS_PER_PAGE = 10;

// Fetch user tasks (for MyTasks page)
export const fetchUserTasks = createAsyncThunk(
  'taskManagement/fetchUserTasks',
  async ({ userEmail, statusFilter, lastVisible = null, isLoadMore = false }, { rejectWithValue }) => {
    try {
      if (!userEmail) return rejectWithValue('User email is required');
      
      let tasksQuery = query(
        collection(db, 'assignedTasks'),
        where('liveStatus', '==', 'active'),
        where('assignees', 'array-contains', userEmail)
      );

      if (statusFilter && statusFilter !== 'all') {
        let queryStatus = statusFilter;
        if (statusFilter === 'new') queryStatus = 'New task';
        if (statusFilter === 'in-progress') queryStatus = 'In progress';
        if (statusFilter === 'completed') queryStatus = 'Completed';
        
        tasksQuery = query(tasksQuery, where('status', '==', queryStatus));
      }

      tasksQuery = query(tasksQuery, orderBy('createdAt', 'desc'));

      if (isLoadMore && lastVisible) {
        tasksQuery = query(tasksQuery, startAfter(lastVisible), limit(TASKS_PER_PAGE));
      } else {
        tasksQuery = query(tasksQuery, limit(TASKS_PER_PAGE));
      }

      const snapshot = await getDocs(tasksQuery);
      
      if (snapshot.empty) {
        return {
          tasks: [],
          lastVisible: null,
          hasMore: false,
          isLoadMore
        };
      }

      const tasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Normalize status for internal use
        statusNormalized: doc.data().status === 'New task' ? 'new' : 
                          doc.data().status === 'In progress' ? 'in-progress' : 
                          'completed'
      }));

      return {
        tasks: tasksData,
        lastVisible: snapshot.docs[snapshot.docs.length - 1],
        hasMore: tasksData.length === TASKS_PER_PAGE,
        isLoadMore
      };
    } catch (error) {
      console.error('Error fetching user tasks:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Fetch all tasks (for AssignTasks admin page)
export const fetchAllTasks = createAsyncThunk(
  'taskManagement/fetchAllTasks',
  async ({ statusFilter, employeeFilter, employees, lastVisible = null, isLoadMore = false }, { rejectWithValue }) => {
    try {
      let tasksQuery = query(
        collection(db, 'assignedTasks'),
        where('liveStatus', '==', 'active')
      );

      if (statusFilter && statusFilter !== 'all') {
        let queryStatus = statusFilter;
        if (statusFilter === 'new') queryStatus = 'New task';
        if (statusFilter === 'in-progress') queryStatus = 'In progress';
        if (statusFilter === 'completed') queryStatus = 'Completed';
        
        tasksQuery = query(tasksQuery, where('status', '==', queryStatus));
      }

      if (employeeFilter && employeeFilter !== 'all' && employees) {
        // Find the employee email
        const employee = employees.find(emp => emp.id === employeeFilter);
        if (employee && employee.email) {
          tasksQuery = query(tasksQuery, where('assignees', 'array-contains', employee.email));
        }
      }

      tasksQuery = query(tasksQuery, orderBy('createdAt', 'desc'));

      const snapshot = await getDocs(tasksQuery);
      
      if (snapshot.empty) {
        return {
          tasks: [],
          lastVisible: null,
          hasMore: false,
          isLoadMore: false
        };
      }

      const tasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Normalize status for internal use
        statusNormalized: doc.data().status === 'New task' ? 'new' : 
                          doc.data().status === 'In progress' ? 'in-progress' : 
                          'completed'
      }));

      return {
        tasks: tasksData,
        lastVisible: null,
        hasMore: false,
        isLoadMore: false
      };
    } catch (error) {
      console.error('Error fetching all tasks:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Fetch task counts
export const fetchTaskCounts = createAsyncThunk(
  'taskManagement/fetchTaskCounts',
  async ({ userEmail, employeeFilter, employees, isAdmin = false }, { rejectWithValue }) => {
    try {
      let baseQuery = query(
        collection(db, 'assignedTasks'),
        where('liveStatus', '==', 'active')
      );
      
      // For user tasks (non-admin)
      if (!isAdmin && userEmail) {
        baseQuery = query(baseQuery, where('assignees', 'array-contains', userEmail));
      }
      
      // For admin filtering by employee
      if (isAdmin && employeeFilter && employeeFilter !== 'all' && employees) {
        const employee = employees.find(emp => emp.id === employeeFilter);
        if (employee && employee.email) {
          baseQuery = query(baseQuery, where('assignees', 'array-contains', employee.email));
        }
      }
      
      // Count all tasks
      const allSnapshot = await getDocs(baseQuery);
      
      // Count new tasks
      const newQuery = query(
        baseQuery,
        where('status', '==', 'New task')
      );
      const newSnapshot = await getDocs(newQuery);
      
      // Count in-progress tasks
      const inProgressQuery = query(
        baseQuery,
        where('status', '==', 'In progress')
      );
      const inProgressSnapshot = await getDocs(inProgressQuery);
      
      // Count completed tasks
      const completedQuery = query(
        baseQuery,
        where('status', '==', 'Completed')
      );
      const completedSnapshot = await getDocs(completedQuery);
      
      return {
        all: allSnapshot.size,
        new: newSnapshot.size,
        inProgress: inProgressSnapshot.size,
        completed: completedSnapshot.size
      };
    } catch (error) {
      console.error('Error fetching task counts:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  userTasks: {
    tasks: [],
    lastVisible: null,
    hasMore: true,
    loading: false,
    error: null
  },
  adminTasks: {
    tasks: [],
    lastVisible: null,
    hasMore: true,
    loading: false,
    error: null
  },
  taskCounts: {
    all: 0,
    new: 0,
    inProgress: 0,
    completed: 0,
    loading: false,
    error: null
  }
};

// Create the slice
const taskManagementSlice = createSlice({
  name: 'taskManagement',
  initialState,
  reducers: {
    clearTaskErrors: (state) => {
      state.userTasks.error = null;
      state.adminTasks.error = null;
      state.taskCounts.error = null;
    },
    clearTasks: (state) => {
      state.userTasks.tasks = [];
      state.userTasks.lastVisible = null;
      state.userTasks.hasMore = true;
      state.adminTasks.tasks = [];
      state.adminTasks.lastVisible = null;
      state.adminTasks.hasMore = true;
    }
  },
  extraReducers: (builder) => {
    // User Tasks
    builder
      .addCase(fetchUserTasks.pending, (state) => {
        state.userTasks.loading = true;
        state.userTasks.error = null;
      })
      .addCase(fetchUserTasks.fulfilled, (state, action) => {
        state.userTasks.loading = false;
        
        if (action.payload.isLoadMore) {
          state.userTasks.tasks = [...state.userTasks.tasks, ...action.payload.tasks];
        } else {
          state.userTasks.tasks = action.payload.tasks;
        }
        
        state.userTasks.lastVisible = action.payload.lastVisible;
        state.userTasks.hasMore = action.payload.hasMore;
      })
      .addCase(fetchUserTasks.rejected, (state, action) => {
        state.userTasks.loading = false;
        state.userTasks.error = action.payload || 'Failed to fetch user tasks';
      });
      
    // Admin Tasks
    builder
      .addCase(fetchAllTasks.pending, (state) => {
        state.adminTasks.loading = true;
        state.adminTasks.error = null;
      })
      .addCase(fetchAllTasks.fulfilled, (state, action) => {
        state.adminTasks.loading = false;
        
        if (action.payload.isLoadMore) {
          state.adminTasks.tasks = [...state.adminTasks.tasks, ...action.payload.tasks];
        } else {
          state.adminTasks.tasks = action.payload.tasks;
        }
        
        state.adminTasks.lastVisible = action.payload.lastVisible;
        state.adminTasks.hasMore = action.payload.hasMore;
      })
      .addCase(fetchAllTasks.rejected, (state, action) => {
        state.adminTasks.loading = false;
        state.adminTasks.error = action.payload || 'Failed to fetch all tasks';
      });
      
    // Task Counts
    builder
      .addCase(fetchTaskCounts.pending, (state) => {
        state.taskCounts.loading = true;
        state.taskCounts.error = null;
      })
      .addCase(fetchTaskCounts.fulfilled, (state, action) => {
        state.taskCounts.loading = false;
        state.taskCounts.all = action.payload.all;
        state.taskCounts.new = action.payload.new;
        state.taskCounts.inProgress = action.payload.inProgress;
        state.taskCounts.completed = action.payload.completed;
      })
      .addCase(fetchTaskCounts.rejected, (state, action) => {
        state.taskCounts.loading = false;
        state.taskCounts.error = action.payload || 'Failed to fetch task counts';
      });
  }
});

export const { clearTaskErrors, clearTasks } = taskManagementSlice.actions;
export default taskManagementSlice.reducer; 